import { Button } from '@/components/ui/button';
import { Factory, TrendingUp, Users, GitBranch, FileText, Server, Download, DoorOpen, Mail } from 'lucide-react';
import { useLang } from '../../contexts/LanguageContext';
import { WALKTHROUGH_MAILTO } from '../../i18n/trust';

const PILLAR_ICONS = [Factory, TrendingUp, Users];
const CONTINUITY_ICONS = [GitBranch, FileText, Server, Download, DoorOpen];

// Named founder profile + key-person-risk answer. Replaces the old
// anonymous "Why Kaizen Kanri" block: a corporate buyer needs a face,
// and then needs to know the platform outlives that face.
export const CreatorSection = () => {
  const { t } = useLang('founder');

  return (
    <section id="creator" className="py-24 px-4 sm:px-6 lg:px-8 bg-[#0a0a0a] relative overflow-hidden">
      <div className="absolute top-1/3 -left-32 w-[420px] h-[420px] bg-[#00CED1]/5 rounded-full blur-[140px] pointer-events-none" />
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-10 lg:gap-14 items-start">
          {/* Portrait */}
          <div className="lg:sticky lg:top-28" data-testid="founder-card">
            <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-white/[0.02]">
              <img
                src="/marketing/creator.jpg"
                alt={t.photoCaption}
                className="w-full h-[440px] object-cover"
                style={{ objectPosition: 'center 22%' }}
                data-testid="founder-photo"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#050a14] via-[#050a14]/80 to-transparent p-5 pt-14">
                <p className="text-lg font-semibold text-white leading-tight">{t.name}</p>
                <p className="text-sm text-[#00CED1]">{t.role}</p>
                <p className="text-xs text-white/45 mt-0.5">{t.roleSub}</p>
              </div>
            </div>
            <Button asChild className="w-full mt-4 bg-[#00CED1] hover:bg-[#00b4b7] text-[#0a0a0a] font-semibold" data-testid="founder-cta">
              <a href={WALKTHROUGH_MAILTO}>
                <Mail className="h-4 w-4 mr-2" /> {t.ctaWalkthrough}
              </a>
            </Button>
          </div>

          {/* Story */}
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#00CED1]">{t.eyebrow}</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-white mb-5">{t.title}</h2>
            <p className="text-lg text-white/65 leading-relaxed mb-6">{t.lead}</p>

            <div className="text-[17px] text-white/60 leading-relaxed mb-10">
              {(t.bio || []).map((p, i) => (
                <p key={i} className="mb-5 last:mb-0">{p}</p>
              ))}
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mb-12" data-testid="experience-pillars">
              {(t.pillars || []).map((p, i) => {
                const Icon = PILLAR_ICONS[i] || Factory;
                return (
                  <div key={p.title} className="p-5 rounded-2xl bg-white/[0.03] border border-white/[0.08]">
                    <Icon className="h-5 w-5 text-[#00CED1] mb-3" />
                    <h3 className="text-sm font-semibold text-white mb-1">{p.title}</h3>
                    <p className="text-xs text-white/45 leading-relaxed">{p.sub}</p>
                  </div>
                );
              })}
            </div>

            {/* Key-person risk */}
            <div className="p-7 rounded-2xl bg-[#0b1220] border border-white/10" data-testid="continuity-panel">
              <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/40">{t.continuityEyebrow}</span>
              <h3 className="mt-2 text-xl font-semibold text-white">{t.continuityTitle}</h3>
              <p className="mt-2 text-sm text-white/55 leading-relaxed mb-6">{t.continuityLead}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {(t.continuity || []).map((c, i) => {
                  const Icon = CONTINUITY_ICONS[i] || GitBranch;
                  return (
                    <div key={c.title} className="flex items-start gap-3" data-testid={`continuity-item-${i}`}>
                      <div className="h-8 w-8 rounded-lg bg-[#00CED1]/10 flex items-center justify-center shrink-0">
                        <Icon className="h-4 w-4 text-[#00CED1]" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-white leading-snug">{c.title}</p>
                        <p className="text-xs text-white/45 leading-relaxed mt-0.5">{c.sub}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="mt-6 pt-5 border-t border-white/5 text-sm text-white/70">{t.closer}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CreatorSection;
